import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, MapPin, Star, Store, Phone, Globe, ArrowRight, Search as SearchIcon } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import api, { fileUrl } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { ISLANDS, ISLAND_NAME } from "../lib/data";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CATS = ["artesanos_mantenimiento", "automotive", "hogar_persona", "enogastronomia",
  "excursiones_actividades", "bienestar", "entretenimiento", "deporte", "restauracion",
  "movilidad", "coffee_drinks"];

export default function Servicios() {
  const { t, lang } = useI18n();
  const [pros, setPros] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [island, setIsland] = useState("all");
  const [municipality, setMunicipality] = useState("all");
  const [loading, setLoading] = useState(true);

  const munis = island !== "all" ? (ISLANDS.find((i) => i.id === island)?.municipalities || []) : [];

  useSeo({
    title: lang === "it" ? "Attività e Servizi alle Isole Canarie - Canarias Market"
      : lang === "en" ? "Activities & Services in the Canary Islands - Canarias Market"
      : "Actividades y Servicios en las Islas Canarias - Canarias Market",
    description: t("services.subtitle"),
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setLoading(true);
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (category !== "all") params.category = category;
      if (island !== "all") params.island = island;
      if (municipality !== "all") params.municipality = municipality;
      api.get("/professionals", { params })
        .then(({ data }) => setPros(data))
        .catch(() => setPros([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handler);
  }, [query, category, island, municipality]);

  return (
    <div>
      {/* Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2537] to-[#008080] text-white">
        <div className="px-5 sm:px-8 py-12 sm:py-16 max-w-4xl">
          <span className="inline-flex items-center gap-2 font-mono-badge text-[11px] text-teal-100 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full mb-4">
            <Store className="h-3.5 w-3.5" /> BUSINESS
          </span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-[1.05]">{t("services.title")}</h1>
          <p className="text-teal-100 text-base sm:text-lg mt-3 max-w-xl">{t("services.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        {/* Filters */}
        <div className="mb-8 space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="services-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("services.searchPlaceholder")} className="h-11 pl-10" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="services-category-filter" className="h-11"><SelectValue placeholder={t("services.category")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("services.allCategories")}</SelectItem>
                  {CATS.map((c) => <SelectItem key={c} value={c}>{t(`monetization.business.cats.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={island} onValueChange={(v) => { setIsland(v); setMunicipality("all"); }}>
                <SelectTrigger data-testid="services-island-filter" className="h-11"><SelectValue placeholder={t("services.island")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("services.allIslands")}</SelectItem>
                  {ISLANDS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {island !== "all" && (
              <div className="flex-1">
                <Select value={municipality} onValueChange={setMunicipality}>
                  <SelectTrigger data-testid="services-municipality-filter" className="h-11"><SelectValue placeholder={t("purchase.municipality")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("services.allMunicipalities")}</SelectItem>
                    {munis.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-center text-slate-400 py-16">{t("common.loading")}</p>
        ) : pros.length === 0 ? (
          <p data-testid="services-empty" className="text-center text-slate-400 py-16">{t("services.empty")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
            {pros.map((p) => (
              <Link key={p.id} to={`/user/${p.id}`} data-testid={`pro-card-${p.id}`}
                className="group bg-white border border-slate-200 rounded-3xl p-5 hover:border-teal-300 hover:shadow-lg transition-all flex flex-col">
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14 border border-slate-100">
                    <AvatarImage src={fileUrl(p.business_logo || p.profile_photo)} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 font-bold">{(p.business_name || p.name || "?")[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-display font-bold text-base flex items-center gap-1 truncate">
                      {p.business_name || p.name}
                      <BadgeCheck className="h-4 w-4 text-[#008080] shrink-0" />
                    </p>
                    {p.rating > 0 && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />{p.rating} · {p.review_count || 0}
                      </p>
                    )}
                  </div>
                </div>

                {p.business_category && (
                  <span className="mt-3 self-start inline-block text-[11px] font-semibold text-[#008080] bg-teal-50 px-2.5 py-1 rounded-full">
                    {t(`monetization.business.cats.${p.business_category}`)}
                  </span>
                )}

                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" />{p.municipality || "—"}{p.island ? `, ${ISLAND_NAME[p.island]}` : ""}</p>
                  {p.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{p.phone}</p>}
                  {p.website && <p className="flex items-center gap-2 truncate"><Globe className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="truncate">{p.website.replace(/^https?:\/\//, "")}</span></p>}
                </div>

                <span className="mt-4 pt-3 border-t border-slate-100 text-sm text-[#008080] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t("services.viewProfile")} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
